import { Dialog } from '../components/Dialog'
import { t } from '../i18n'
import type { MarketplaceItem } from '../api/types'
import type { PosAccountLine } from '../lightspeed/types'

function formatChf(cents?: number): string {
  if (typeof cents !== 'number') return ''
  return `CHF ${(cents / 100).toFixed(2)}`
}

/** "Quel article offrir ?" — pick WHICH line on the open order the reward's discount lands on.
 *
 * Only shown when the order holds more than one line of the reward's linked item. That is a real
 * case at a till: two of the same drink rung up separately, the same dish for two seats, a course
 * split across rounds. The lines are not interchangeable — they can carry different modifiers and
 * therefore different amounts, and the discount is real money off a live sale — so the cashier
 * chooses instead of the app silently taking the first one.
 *
 * A single match skips this entirely; zero matches never reaches here (the claim dialog's
 * "add the product" path handles an item that is not on the ticket).
 */
export function OrderLinePicker({
  item,
  lines,
  busy = false,
  onPick,
  onCancel,
}: {
  item: MarketplaceItem
  lines: PosAccountLine[]
  busy?: boolean
  onPick: (line: PosAccountLine) => void
  onCancel: () => void
}) {
  return (
    <Dialog title={t('whichLineToOffer')} onDismiss={onCancel}>
      <p className="mt-4 text-[17px] text-ink-soft">
        {t('whichLineToOfferHint').replace('{reward}', item.label)}
      </p>

      <div className="mt-6 flex max-h-[340px] flex-col gap-3 overflow-y-auto pr-1">
        {lines.map((line, i) => (
          <button
            key={line.lineId}
            type="button"
            disabled={busy}
            onClick={() => onPick(line)}
            className="flex min-h-[68px] w-full items-center justify-between gap-4 rounded-lg border border-line bg-white px-5 text-left transition hover:border-brand hover:bg-brand-50 disabled:opacity-60"
          >
            <span className="min-w-0">
              <span className="block truncate text-[19px] font-semibold text-ink">
                {line.name ?? item.label}
              </span>
              {/* The order position is what the cashier can actually see on the POS ticket, so it
                * is the only reliable way to tell two identical lines apart. */}
              <span className="block text-[14px] text-ink-soft">
                {t('orderLineNumber').replace('{n}', String(i + 1))}
                {typeof line.quantity === 'number' && line.quantity !== 1
                  ? ` · ×${line.quantity}`
                  : ''}
              </span>
            </span>
            <span className="shrink-0 text-[17px] font-semibold text-ink-soft">
              {formatChf(line.priceCents)}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={onCancel}
        className="mt-6 w-full rounded-lg border border-line bg-white py-[18px] text-[21px] font-semibold text-ink-soft transition hover:bg-canvas disabled:opacity-60"
      >
        {t('cancel')}
      </button>
    </Dialog>
  )
}
